import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { categoriesAPI } from '../../services/api';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    order: 0,
    parent_id: null
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, formData);
      } else {
        await categoriesAPI.create(formData);
      }
      loadCategories();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) return;
    
    try {
      await categoriesAPI.delete(id);
      loadCategories();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData(category);
    } else {
      setEditingCategory(null);
      setFormData({ name: '', slug: '', description: '', icon: '', order: 0, parent_id: null });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Catégories</h1>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 transition"
          >
            <Plus className="w-5 h-5" />
            Nouvelle Catégorie
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <div key={category.id} className="bg-blue-800 rounded-lg p-6 border border-blue-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{category.icon}</div>
                  <div className="flex gap-2">
                    <Link
                      to={`/admin/products/new?category=${category.id}`}
                      className="p-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition"
                      title="Ajouter un produit"
                    >
                      <Plus className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => openModal(category)}
                      className="p-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="text-white font-bold text-xl mb-2">{category.name}</h3>
                <p className="text-gray-400 text-sm">{category.description}</p>
                <div className="mt-4 text-cyan-400 text-sm">
                  Slug: {category.slug}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-blue-800 rounded-lg p-6 max-w-md w-full border border-blue-700">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingCategory ? 'Modifier' : 'Nouvelle'} Catégorie
                </h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-white mb-2">Catégorie Parente (optionnel)</label>
                  <select
                    value={formData.parent_id || ''}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  >
                    <option value="">Aucune (Catégorie principale)</option>
                    {categories.filter(cat => !cat.parent_id && cat.id !== editingCategory?.id).map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <p className="text-gray-400 text-sm mt-1">
                    Choisissez une catégorie parente pour créer une sous-catégorie
                  </p>
                </div>
              
                <div>
                  <label className="block text-white mb-2">Nom *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Icône (emoji)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="🎮"
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Ordre</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 flex items-center justify-center gap-2 bg-cyan-500 text-white py-2 rounded hover:bg-cyan-600 transition"
                  >
                    <Save className="w-5 h-5" />
                    Enregistrer
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-600 text-white py-2 rounded hover:bg-gray-700 transition"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Categories;
